import * as React from "react";
import { SheetSettledProvider } from "@/components/ui/sheetSettled";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";
import {
  SheetGrabZone,
  SHEET_GRAB_ZONE_CLASS,
  SHEET_RADIUS_TOP_CLASS,
  SHEET_MAX_H_CLASS,
  SHEET_ANIM_RISE,
  SHEET_OVERLAY_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";

/**
 * Cajón adaptativo: hoja inferior en el teléfono (<768) y ventana centrada en
 * tableta y escritorio. Misma API en los dos sitios.
 *
 * Antes esto montaba vaul abajo y Radix arriba, con un contexto y un
 * `matchMedia` para elegir. Eran DOS motores de animación en la misma app: vaul
 * mueve el panel con su propio muelle y transformaciones en línea, así que las
 * clases `animate-in` compartidas no le hacían nada y estas ventanas entraban
 * distintas de todas las demás. Ahora es Radix en los dos tamaños, con las
 * clases responsive de siempre — una sola animación, la de SHEET_ANIM_RISE.
 *
 * Efecto secundario que se gana: al no cambiar de primitiva a mitad de camino,
 * cruzar los 768 px con el cajón abierto ya no lo desmonta.
 *
 * Lo que se pierde: el deslizar-para-cerrar propio de vaul. Lo sustituye
 * SheetGrabZone (arrastrar la franja del tirador), que es el mismo gesto en
 * TODAS las hojas y además no compite con el scroll del contenido — por eso los
 * consumidores ya no necesitan `data-vaul-no-drag`.
 */

const Drawer = ({
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & {
  /** Aceptado por compatibilidad con la API de vaul; ya no hace nada. */
  shouldScaleBackground?: boolean;
}) => {
  // `shouldScaleBackground` se descarta a propósito: era de vaul.
  const { shouldScaleBackground: _ignored, ...rootProps } = props;
  return <DialogPrimitive.Root {...rootProps}>{children}</DialogPrimitive.Root>;
};
Drawer.displayName = "Drawer";

const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;
const DrawerPortal = DialogPrimitive.Portal;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(SHEET_OVERLAY_CLASS, className)} {...props} />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Cuánto ocupa en el teléfono (ver `SheetFit`). Como el interior lo monta el
   * consumidor, `auto` mide el hijo marcado con `data-sheet-scroll` — y si no
   * hay ninguno, el panel. Las fichas de detalle largas pasan `expanded`.
   */
  fit?: SheetFit;
  /**
   * El VELO que oscurece lo de detrás. Se puede quitar (`false`) para una hoja
   * que se abre ENCIMA de algo que hay que poder seguir usando —el mapa, con el
   * detalle de un sensor abierto—. Quien lo quite tiene que abrir además el
   * `<Drawer>` con `modal={false}`: si no, Radix sigue apagando los toques del
   * resto de la página aunque el velo no se vea.
   */
  overlay?: boolean;
  /** Color del tirador. Sobre una hoja oscura, el de siempre no se ve. */
  handleClassName?: string;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, children, fit = "auto", overlay = true, handleClassName, onOpenAutoFocus, ...props }, ref) => {
  // El arrastre cierra por el mismo camino que la X: un Close de Radix.
  const grabCloseRef = React.useRef<HTMLButtonElement>(null);

  const [panel, setPanelNode] = React.useState<HTMLDivElement | null>(null);
  const setPanel = React.useCallback(
    (node: HTMLDivElement | null) => {
      setPanelNode(node);
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );
  const fitClass = useSheetFitClass({ fit, host: panel });

  return (
    <DrawerPortal>
      {overlay && <DrawerOverlay />}
      <DialogPrimitive.Content
        ref={setPanel}
        onOpenAutoFocus={createTouchOpenAutoFocusHandler(onOpenAutoFocus)}
        aria-describedby={undefined}
        className={cn(
          // Teléfono: anclada abajo, a todo el ancho. El `pt-4` es el hueco del
          // tirador, que ahora va en absoluto por encima (antes ocupaba sitio en
          // el flujo y lo abría él solo).
          "fixed inset-x-0 bottom-0 z-50 flex h-auto flex-col border bg-white max-md:pt-4",
          SHEET_MAX_H_CLASS,
          SHEET_RADIUS_TOP_CLASS,
          SHEET_ANIM_RISE,
          // Tableta y escritorio: ventana centrada. Los desplazamientos del zoom
          // ya vienen en SHEET_ANIM_RISE con los mismos -50%/-48% que este
          // translate, así que no entra en diagonal.
          "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
          "md:w-[560px] md:max-w-[calc(100vw-3rem)] md:max-h-[85vh] md:overflow-hidden",
          // Literal a propósito: Tailwind lee el código fuente, así que una clase
          // construida en tiempo de ejecución ("md:" + constante) no se generaría.
          "md:shadow-[0_24px_60px_-12px_rgba(15,39,68,0.35)] md:rounded-[24px]",
          className,
          // AL FINAL, después del `className` del consumidor. Ver `useSheetFitClass`.
          fitClass,
        )}
        {...props}
      >
        {/* La franja de arrastre: toda la parte de arriba, no solo la rayita.
            Va en absoluto para que ese ancho no empuje el contenido — el hueco
            lo pone el `pt-4` del panel. */}
        <SheetGrabZone
          onClose={() => grabCloseRef.current?.click()}
          className={SHEET_GRAB_ZONE_CLASS}
          handleClassName={handleClassName}
        />
        <DialogPrimitive.Close ref={grabCloseRef} className="hidden" aria-hidden tabIndex={-1} />
        {/* `open` en duro: este contenido lo monta el portal de Radix cuando la
            hoja se abre y lo desmonta al cerrarse, así que montarse ES abrirse.
            Cualquier hijo puede leerlo con `useSheetIsSettled()` o envolverse en
            `<DeferUntilSettled>` para no construirse durante la subida. */}
        <SheetSettledProvider open>{children}</SheetSettledProvider>
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
